import React, { useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'reactstrap';
import Page from './Pages/Page';
import { getFilteredQuestions } from 'helpers/sf_utils';
import { QUOTE_FUNNEL_QUESTION_DEPENDS_ON_TYPE } from 'helpers/sf_constants';
import _ from 'lodash';
import useQuery from 'hooks/useQuery';
import { makeLabelForRepeatableQuestions } from 'helpers/quote_funnel_helper';

const PageGroup = ({ page_group, answers, onChangeEvent, nextPageGroup, isLastPageGroup, 
  onPageChange, quote_funnel_questions_loading, review, tcpa_disclaimer_text 
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [pendingCallbacks, setPendingCallbacks] = useState([]);
  const [callUpdateAPI, setCallUpdateAPI] = useState(false);

  const query = useQuery();
  const page_current_index = parseInt(query.get('page_current_index') || 0);

  useEffect(() => {
    if (!pendingCallbacks.length) {
      return;
    }

    pendingCallbacks.forEach(callback => callback(page_group));
    setPendingCallbacks([]);
  }, [page_group]);

  useEffect(() => {
    if (callUpdateAPI) {
      onPageChange('edit', page_current_index);
      setCallUpdateAPI(false);
    }
  }, [callUpdateAPI]);

  const handleNextPageCb = (page_group) => {
    const current_pages = page_group?.pages || [];
    const page_length = current_pages?.length || 0;
    if (page_length > page_current_index + 1) {
      return;
    }
    nextPageGroup();
    setCurrentIndex(0);
  }

  const handlePageChange = (request_duplicate_pages) => {
    if (request_duplicate_pages) {
      setPendingCallbacks([...pendingCallbacks, handleNextPageCb]);
    } else {
      handleNextPageCb(page_group);
    }
    setCallUpdateAPI(true);
  }

  const handleChange = (val, index) => {
    let new_pages = [];
    const existing_pages = page_group?.pages?.map((page, idx) => {
      if (idx !== index) {
        return page;
      }
      return val;
    })

    if (val?.request_duplicate_pages) {
      const current_pages = page_group?.pages || [];
      const page_length = current_pages?.length || 0;
      const filtered_pages = current_pages.filter(page => page?.repeatable_template).sort((a, b) => a.order - b.order) // should be mapped
      const current_questions = val?.question_groups?.map(question_group => question_group?.questions?.map(question => ({ field_name: question?.field_name, repeatable_type: question?.repeatable_type, array_field: question?.array_field, value: question?.value || "" }))).flat(Infinity);
      const repeatable_question_fields = current_questions?.find(q => q?.repeatable_type === "add_another_instance_prompt") || null;
      const current_index = page_length / filtered_pages.length
      new_pages = filtered_pages?.map((page, index) => {
        return ({
          ...page,
          repeatable_template: false,
          order: page_length + index + 1,
          question_groups: page?.question_groups?.map(question_group => {
          const current_group_questions = question_group?.questions?.map(question => ({ repeatable_type: question?.repeatable_type })).flat(Infinity);
          const repeatable_type_add_instance = current_group_questions?.find(cgq => cgq?.repeatable_type === "add_another_instance_prompt") 
            return ({
              ...question_group,
              label: repeatable_type_add_instance?.repeatable_type === "add_another_instance_prompt" ? question_group.label : makeLabelForRepeatableQuestions(current_index, repeatable_question_fields?.array_field, question_group?.label),
              questions: question_group?.questions?.map(question => {
                return ({
                  ...question,
                  value: null,
                  field_name: (`${question?.array_field}_${question?.original_field_name}_${current_index + 1}`),
                  depends_on_type: repeatable_question_fields ? QUOTE_FUNNEL_QUESTION_DEPENDS_ON_TYPE.EQUAL_TO : question.depends_on_type,
                  depends_on_value: repeatable_question_fields?.value || question.depends_on_value,
                  depends_on: repeatable_question_fields?.field_name || question.depends_on,
                })
              })
            })
          })
        })
      })
    }
    onChangeEvent({
      ...page_group,
      pages: [...existing_pages, ...new_pages],
    })
  };

  const activePage = useMemo(() => {
    // if (!page_group || !page_group?.pages || !Array.isArray(page_group?.pages) || page_group?.pages?.length === 0) {
    //   return null; // Handle the case where the page_group or pages array is empty
    // }

    const sorted_pages = (page_group?.pages || [])?.sort((a, b) => a.order - b.order) // should be mapped

    const current_page = sorted_pages[page_current_index] || [];

    // if (!current_page || !current_page.question_groups || !Array.isArray(current_page.question_groups) || current_page.question_groups.length === 0) {
    //   return null; // Handle the case where the current page or question_groups array is empty
    // }
    const question_types = [];
    const new_current_page = {
      ...current_page,
      page_group_order: page_group.order,
      question_groups: (current_page?.question_groups || []).map(q_gr => {
        const q_types = q_gr?.questions.map(q => q?.question_type);
        question_types.push(...q_types);
        if (!q_gr.questions || !Array.isArray(q_gr.questions) || q_gr.questions.length === 0) {
          return q_gr; // Handle the case where there are no questions in the group
        }

        const filteredQuestions = getFilteredQuestions(q_gr, answers);

        return {
          ...q_gr,
          questions: filteredQuestions,
        };
      }).sort((a, b) => a.order - b.order),
      question_types: _.uniq((_.flatten(question_types || [])))
    };
    return new_current_page
  }, [answers, page_current_index, page_group]);


  const sorted_pages = (page_group?.pages || [])?.sort((a, b) => a.order - b.order) // should be mapped

  // Make sure to handle the case where activePage could be null or an empty array in your component logic.

  return (
    <Row id={`quote-form-page-group-${nextPageGroup}-${currentIndex}`}>
      <Col md={12} key={currentIndex}>
        <Page
          quote_funnel_questions_loading={quote_funnel_questions_loading}
          nextPage={handlePageChange}
          answers={answers}
          index={page_current_index}
          key={`page_${page_current_index}`}
          page={activePage}
          onChangeEvent={(value) => handleChange(value, page_current_index)}
          isLastPage={sorted_pages?.length === page_current_index + 1}
          isLastPageGroup={isLastPageGroup}
          review={review}
          tcpa_disclaimer_text={tcpa_disclaimer_text}
        />
      </Col>
    </Row>
  )
}

export default PageGroup
